import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Fingerprint, KeyRound, Mic, Smartphone, Check, Lock, ShieldCheck } from "lucide-react";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import { hubApi } from "@/api/dataHub";
import { enrollPasskey, vaultUnlockPasskey, passkeySupported, passkeyErrorMessage } from "@/lib/passkey";

/* Fixed priority order + friendly labels. 2FA is the optional 4th method. */
const METHODS = {
  biometric: { label: "Fingerprint / Face ID", sub: "Your device's secure sensor", Icon: Fingerprint },
  pin: { label: "PIN code", sub: "4 to 6 digits", Icon: KeyRound },
  voice: { label: "Voice passphrase", sub: "Record a short phrase", Icon: Mic },
  totp: { label: "Authenticator (2FA)", sub: "Optional · needs an app", Icon: Smartphone },
};
const ORDER = ["biometric", "pin", "voice", "totp"];

const shell = "absolute inset-0 z-30 flex flex-col bg-charcoal";
const shellStyle = { boxShadow: "inset 0 0 0 3px #D4AF37" };
const gold = "bg-gold text-black border-2 border-charcoal font-mono text-xs font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50";
const field = "w-full bg-white/10 border-2 border-gold rounded-xl px-4 py-3 font-mono text-2xl text-center text-white tracking-[0.5em] outline-none focus:ring-2 focus:ring-gold";

/* Compact 3-second recorder used for both enrol and unlock. */
function useRecorder() {
  const [rec, setRec] = useState(false);
  const record = () =>
    new Promise((resolve, reject) => {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        const chunks = [];
        const m = new MediaRecorder(stream);
        m.ondataavailable = (e) => chunks.push(e.data);
        m.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setRec(false); resolve(new Blob(chunks, { type: "audio/webm" })); };
        m.start(); setRec(true);
        setTimeout(() => { if (m.state !== "inactive") m.stop(); }, 3200);
      }).catch(() => { setRec(false); reject(new Error("mic")); });
    });
  return { rec, record };
}

function Header({ title, step, onBack }) {
  return (
    <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
      <button onClick={onBack} data-testid="vault-v2-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
      <div className="leading-none">
        <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">{step}</p>
        <h1 className="font-serif text-2xl text-white">{title}</h1>
      </div>
    </header>
  );
}

/* ============================ SETUP WIZARD ============================== */
export function VaultSetupWizard({ status, onRefresh, onArmed, onBack }) {
  const methods = status?.methods || {};
  const count = ORDER.filter((m) => methods[m]).length;
  const min = status?.min_required || 3;
  const [busy, setBusy] = useState("");
  const [pin, setPin] = useState("");
  const [totp, setTotp] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const { rec, record } = useRecorder();

  const refresh = () => Promise.resolve(onRefresh?.());

  const doBiometric = async () => {
    if (!passkeySupported()) { toast("This device can't do fingerprint — use PIN + Voice.", { icon: "🔒" }); return; }
    setBusy("biometric");
    try { await enrollPasskey(); toast.success("Fingerprint unlock is on 🔓"); await refresh(); }
    catch (e) { toast(passkeyErrorMessage(e), { icon: "🔒" }); }
    finally { setBusy(""); }
  };
  const doPin = async () => {
    if (!/^\d{4,6}$/.test(pin)) { toast.error("PIN must be 4 to 6 digits"); return; }
    setBusy("pin");
    try { await vaultSecurityApi.pinSet(pin); setPin(""); toast.success("PIN saved ✓"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save PIN"); }
    finally { setBusy(""); }
  };
  const doVoice = async () => {
    setBusy("voice");
    try { const blob = await record(); const r = await hubApi.enrollVoice(blob); toast.success(r.message || "Voice saved ✓"); await refresh(); }
    catch { toast.error("Please allow the microphone and try again"); }
    finally { setBusy(""); }
  };
  const startTotp = async () => {
    setBusy("totp");
    try { const r = await vaultSecurityApi.totpInit(); setTotp(r); }
    catch { toast.error("Couldn't start authenticator setup"); }
    finally { setBusy(""); }
  };
  const confirmTotp = async () => {
    setBusy("totp");
    try { await vaultSecurityApi.totpConfirm(totpCode.trim()); setTotp(null); setTotpCode(""); toast.success("Authenticator linked ✓"); await refresh(); }
    catch { toast.error("Wrong code — try again"); }
    finally { setBusy(""); }
  };

  const arm = async () => {
    setBusy("arm");
    try { await vaultSecurityApi.setupComplete(); toast.success("Your vault is set 🔐"); onArmed(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Set up at least 3 locks first"); }
    finally { setBusy(""); }
  };

  return (
    <div className={shell} style={shellStyle} data-testid="vault-setup-wizard">
      <Header step="First-time setup" title="Set Up Your Locks" onBack={onBack} />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
        <div className="border-2 border-gold/40 rounded-2xl bg-gold/[0.06] p-3.5" data-testid="vault-setup-progress">
          <p className="font-mono text-[11px] text-white/70 leading-relaxed">Set up any <span className="text-gold font-black">{min}</span> locks to arm your vault. They unlock in this order: fingerprint, then PIN, then voice.</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gold" data-testid="vault-setup-count">{count} of {min} set</p>
        </div>

        {ORDER.map((key) => {
          const m = METHODS[key]; const on = methods[key]; const loading = busy === key;
          return (
            <div key={key} className={`border-2 rounded-2xl p-3.5 ${on ? "border-emerald-400 bg-emerald-400/10" : "border-black bg-white/5"}`} data-testid={`vault-setup-${key}`}>
              <div className="flex items-center gap-3">
                {loading ? <Loader2 size={20} className="animate-spin text-gold" /> : <m.Icon size={20} className={on ? "text-emerald-400" : "text-gold"} />}
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm font-bold text-white">{m.label}{key === "totp" && <span className="text-white/40 text-[10px]"> · optional</span>}</p>
                  <p className={`font-mono text-xs ${on ? "text-emerald-300" : "text-white/45"}`}>{on ? "Set ✓" : m.sub}</p>
                </div>
                {on
                  ? <span className="grid h-6 w-6 place-items-center rounded-full border-2 border-black bg-emerald-400"><Check size={13} strokeWidth={3.5} className="text-black" /></span>
                  : key === "biometric" ? <button onClick={doBiometric} disabled={loading} className={`${gold} px-3 py-2 rounded-full`} data-testid="vault-setup-biometric-btn">Turn on</button>
                  : key === "voice" ? <button onClick={doVoice} disabled={loading || rec} className={`${gold} px-3 py-2 rounded-full`} data-testid="vault-setup-voice-btn">{rec ? "Listening…" : "Record"}</button>
                  : key === "totp" && !totp ? <button onClick={startTotp} disabled={loading} className={`${gold} px-3 py-2 rounded-full`} data-testid="vault-setup-totp-btn">Set up</button>
                  : null}
              </div>
              {key === "pin" && !on && (
                <div className="mt-3 flex gap-2">
                  <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="• • • •" data-testid="vault-setup-pin-input" className={`${field} flex-1`} />
                  <button onClick={doPin} disabled={busy === "pin"} className={`${gold} px-4 rounded-xl`} data-testid="vault-setup-pin-save">{busy === "pin" ? <Loader2 size={14} className="animate-spin" /> : "Save"}</button>
                </div>
              )}
              {key === "totp" && totp && (
                <div className="mt-3 space-y-2">
                  <div className="border-2 border-white/15 rounded-xl p-2.5 break-all font-mono text-[10px] text-white/70" data-testid="vault-setup-totp-secret">{totp.secret || totp.otpauth_uri}</div>
                  <div className="flex gap-2">
                    <input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" data-testid="vault-setup-totp-code" className="flex-1 bg-white/5 border-2 border-white/15 rounded-xl px-3 py-2.5 font-mono text-base tracking-[0.3em] text-center text-white outline-none focus:border-gold" />
                    <button onClick={confirmTotp} disabled={totpCode.length !== 6} className={`${gold} px-4 rounded-xl`} data-testid="vault-setup-totp-confirm">Link</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="shrink-0 border-t-2 border-gold/20 bg-charcoal px-4 py-4">
        <button onClick={arm} disabled={count < min || busy === "arm"} data-testid="vault-setup-arm-btn"
          className="w-full flex items-center justify-center gap-2 bg-black text-gold font-black font-mono text-sm uppercase tracking-[0.15em] py-5 rounded-2xl border-4 border-gold shadow-[5px_5px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy === "arm" ? <Loader2 size={20} className="animate-spin" /> : <Lock size={20} />} {count < min ? `Set ${min - count} more to arm` : "Arm my vault"}
        </button>
      </div>
    </div>
  );
}

/* ============================ UNLOCK SCREEN ============================= */
export function VaultUnlockV2({ status, onUnlock, onBack, onLockout }) {
  const enrolled = (status?.enrolled_order || []).filter((m) => m !== "totp" || status?.methods?.totp);
  const [active, setActive] = useState(enrolled[0] || "pin");
  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { rec, record } = useRecorder();
  useEffect(() => { setActive((cur) => (enrolled.includes(cur) ? cur : (enrolled[0] || "pin"))); /* eslint-disable-next-line */ }, [status]);

  const doBiometric = async () => {
    setBusy(true);
    try { await vaultUnlockPasskey(); await onUnlock(); }
    catch (e) { toast(passkeyErrorMessage(e), { icon: "🔒" }); }
    finally { setBusy(false); }
  };
  const doPin = async () => {
    setBusy(true);
    try { await vaultSecurityApi.sessionPinUnlock(pin); setPin(""); await onUnlock(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "vault_locked") { onLockout?.(d.seconds_remaining || 60); return; }
      toast.error(d?.message || "Incorrect PIN");
    } finally { setBusy(false); }
  };
  const doVoice = async () => {
    setBusy(true);
    try { const blob = await record(); await hubApi.verifyVoice(blob); await onUnlock(); }
    catch (e) { toast.error(e?.response?.data?.detail || "We couldn't match your voice — try again"); }
    finally { setBusy(false); }
  };
  const doTotp = async () => {
    setBusy(true);
    try { await vaultSecurityApi.totpChallenge(code.trim()); setCode(""); await onUnlock(); }
    catch { toast.error("Wrong code — try again"); }
    finally { setBusy(false); }
  };

  const m = METHODS[active] || METHODS.pin;
  return (
    <div className={shell} style={shellStyle} data-testid="vault-unlock-v2">
      <Header step="Secure Vault" title="Unlock" onBack={onBack} />
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
        <span className="w-24 h-24 rounded-[28px] border-4 border-gold bg-white/10 grid place-items-center shadow-[5px_5px_0px_#000]"><m.Icon size={46} className="text-gold" /></span>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{m.label}</p>
          <h2 className="mt-1 font-serif text-3xl text-white leading-tight">{active === "biometric" ? "Use your fingerprint" : active === "pin" ? "Enter your PIN" : active === "voice" ? "Say your passphrase" : "Enter your code"}</h2>
        </div>

        {active === "biometric" && (
          <button onClick={doBiometric} disabled={busy} data-testid="vault-unlock-biometric" className={`${gold} w-full max-w-[300px] py-4 rounded-2xl flex items-center justify-center gap-2`}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Fingerprint size={16} />} Unlock with fingerprint</button>
        )}
        {active === "pin" && (
          <div className="w-full max-w-[300px] space-y-3">
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="• • • •" data-testid="vault-unlock-pin-input" className={field} onKeyDown={(e) => e.key === "Enter" && pin.length >= 4 && doPin()} />
            <button onClick={doPin} disabled={busy || pin.length < 4} data-testid="vault-unlock-pin-btn" className={`${gold} w-full py-4 rounded-2xl flex items-center justify-center gap-2`}>{busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Unlock</button>
          </div>
        )}
        {active === "voice" && (
          <button onClick={doVoice} disabled={busy || rec} data-testid="vault-unlock-voice" className={`${gold} w-full max-w-[300px] py-4 rounded-2xl flex items-center justify-center gap-2`}>{rec ? <><Loader2 size={16} className="animate-spin" /> Listening…</> : <><Mic size={16} /> Speak to unlock</>}</button>
        )}
        {active === "totp" && (
          <div className="w-full max-w-[300px] space-y-3">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" data-testid="vault-unlock-totp-input" className={field} />
            <button onClick={doTotp} disabled={busy || code.length !== 6} data-testid="vault-unlock-totp-btn" className={`${gold} w-full py-4 rounded-2xl`}>Unlock</button>
          </div>
        )}

        {enrolled.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2" data-testid="vault-unlock-switch">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Or use</span>
            {enrolled.filter((k) => k !== active).map((k) => (
              <button key={k} onClick={() => setActive(k)} data-testid={`vault-unlock-switch-${k}`} className="font-mono text-[10px] uppercase tracking-widest text-gold underline underline-offset-4">{METHODS[k].label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
